// src/components/session/SessionDetails.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  MessageSquare,
  User,
  AlertCircle,
  Download,
  Copy,
} from "lucide-react";
import api from "../../api"; // Use centralized API client

export default function SessionDetails() {
  const { id } = useParams(); // route: /session/:id
  const navigate = useNavigate();

  // ---- Brand (match Session.js)
  const BRAND = {
    teal: "#55c5d0",
    tealHover: "#48b7c3",
  };

  // ---- State
  const [sessionMeta, setSessionMeta] = useState(null);
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaError, setMetaError] = useState("");

  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(true);
  const [msgError, setMsgError] = useState("");

  const [msgPage, setMsgPage] = useState(1);
  const [msgTotalPages, setMsgTotalPages] = useState(1);
  const PAGE_SIZE = 25;

  // ---- Data
  const fetchSessionMeta = async () => {
    setMetaLoading(true);
    setMetaError("");
    setSessionMeta(null);
    try {
      const res = await api.get(`api/chatbot/sessions/${id}/`, {
        timeout: 30000,
      });
      let meta = res.data;
      if (meta?.data) meta = meta.data;
      setSessionMeta(meta || null);
    } catch (e) {
      const status = e?.response?.status;
      // Many backends don't expose a details endpoint; suppress 404 noise
      if (status === 404) {
        setMetaError("");
        setSessionMeta(null);
      } else {
        const detail =
          e.response?.data?.detail ||
          e.response?.data?.message ||
          "Unable to fetch session details.";
        setMetaError(detail);
      }
    } finally {
      setMetaLoading(false);
    }
  };

  const fetchMessages = async (page = 1) => {
    setMsgLoading(true);
    setMsgError("");
    try {
      const res = await api.get(
        `api/chatbot/sessions/${id}/messages/?page=${page}&page_size=${PAGE_SIZE}`,
        { timeout: 30000 }
      );

      let items = [];
      let count = null;

      if (Array.isArray(res.data)) {
        items = res.data;
      } else if (Array.isArray(res.data?.results)) {
        items = res.data.results;
        count = res.data.count ?? null;
      } else if (Array.isArray(res.data?.data)) {
        items = res.data.data;
        count = res.data.count ?? res.data.total ?? null;
      } else if (Array.isArray(res.data?.messages)) {
        items = res.data.messages;
        count = res.data.count ?? res.data.total ?? items.length;
      }

      setMessages(items || []);
      if (count != null) {
        setMsgTotalPages(Math.max(1, Math.ceil(count / PAGE_SIZE)));
      } else {
        setMsgTotalPages(items.length < PAGE_SIZE && page === 1 ? 1 : page);
      }
      setMsgPage(page);
    } catch (e) {
      console.error("Error fetching messages:", e);
      const detail =
        e.response?.data?.detail ||
        e.response?.data?.message ||
        "Failed to fetch session messages.";
      setMsgError(detail);
    } finally {
      setMsgLoading(false);
    }
  };

  useEffect(() => {
    fetchSessionMeta();
    fetchMessages(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ---- UI helpers
  const renderMessage = (m) => {
    const role = (m.role || m.sender || "message").toLowerCase();
    const content = m.content ?? m.text ?? m.message ?? "";
    const time = m.created_at ? new Date(m.created_at).toLocaleString() : null;

    return (
      <div key={m.id || `${role}-${Math.random()}`} className="flex gap-3 py-3">
        <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center shrink-0">
          {role === "user" ? <User className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
        </div>
        <div className="flex-1">
          <div className="text-[11px] text-gray-500 mb-1 capitalize">{role}</div>
          <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">
            {content || <span className="text-gray-400 italic">[empty message]</span>}
          </div>
          {time && <div className="text-[11px] text-gray-400 mt-1">{time}</div>}
        </div>
      </div>
    );
  };

  const handleCopyTranscript = async () => {
    try {
      const lines = messages.map((m) => {
        const role = (m.role || m.sender || "message").toUpperCase();
        const content = m.content ?? m.text ?? m.message ?? "";
        return `${role}: ${content}`;
      });
      await navigator.clipboard.writeText(lines.join("\n\n"));
      alert("Transcript copied to clipboard.");
    } catch {
      alert("Unable to copy transcript.");
    }
  };

  const handleDownloadJSON = () => {
    const payload = { session: sessionMeta, messages };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `session-${id}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const goPage = (delta) => {
    const target = msgPage + delta;
    if (target < 1 || target > msgTotalPages) return;
    fetchMessages(target);
  };

  // Only show meta error if there are no messages (true failure)
  const showMetaError = !!metaError && !msgLoading && messages.length === 0;

  return (
    <section className="space-y-6 p-6 font-inter bg-slate-50 min-h-screen">
      {/* Header actions — styled like Session.js buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 rounded-xl text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={handleCopyTranscript}
            className="px-4 py-2 rounded-xl text-white"
            style={{ backgroundColor: BRAND.teal }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = BRAND.tealHover)}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = BRAND.teal)}
          >
            <span className="inline-flex items-center gap-2">
              <Copy className="w-4 h-4" /> Copy transcript
            </span>
          </button>
          <button
            onClick={handleDownloadJSON}
            className="px-4 py-2 rounded-xl text-white"
            style={{ backgroundColor: BRAND.teal }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = BRAND.tealHover)}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = BRAND.teal)}
          >
            <span className="inline-flex items-center gap-2">
              <Download className="w-4 h-4" /> Download JSON
            </span>
          </button>
        </div>
      </div>

      {/* Meta Card — same card style as Session.js table container */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-semibold text-slate-900">Session #{id}</h1>
          {metaLoading && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Loading details…
            </div>
          )}
        </div>

        {showMetaError ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{metaError}</span>
          </div>
        ) : sessionMeta ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-gray-500">Flow</div>
              <div className="font-medium">{sessionMeta.flow || "—"}</div>
            </div>
            <div>
              <div className="text-gray-500">Status</div>
              <div className="font-medium">{sessionMeta.status || "—"}</div>
            </div>
            <div>
              <div className="text-gray-500">Created</div>
              <div className="font-medium">
                {sessionMeta.created_at ? new Date(sessionMeta.created_at).toLocaleString() : "—"}
              </div>
            </div>

            {sessionMeta.context && (
              <>
                <div>
                  <div className="text-gray-500">Name</div>
                  <div className="font-medium">{sessionMeta.context.name || "—"}</div>
                </div>
                <div>
                  <div className="text-gray-500">Email</div>
                  <div className="font-medium">{sessionMeta.context.email || "—"}</div>
                </div>
                <div>
                  <div className="text-gray-500">Contact</div>
                  <div className="font-medium">{sessionMeta.context.contact || "—"}</div>
                </div>
                <div>
                  <div className="text-gray-500">Budget</div>
                  <div className="font-medium">{sessionMeta.context.budget || "—"}</div>
                </div>
              </>
            )}
          </div>
        ) : (
          !metaLoading && <div className="text-gray-600 text-sm">No session detail available.</div>
        )}
      </div>

      {/* Messages Card — header strip uses brand color to echo table header look */}
      <div className="rounded-2xl shadow-md border border-gray-100 overflow-hidden bg-white">
        <div
          className="px-5 py-3 text-white text-sm font-semibold tracking-wide uppercase"
          style={{ backgroundColor: BRAND.teal }}
        >
          Messages
        </div>

        <div className="p-5">
          {msgLoading && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}

          {msgError ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <span>{msgError}</span>
            </div>
          ) : messages.length > 0 ? (
            <>
              <div className="divide-y">{messages.map((m) => renderMessage(m))}</div>

              {msgTotalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    onClick={() => goPage(-1)}
                    disabled={msgPage === 1 || msgLoading}
                    className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <div className="text-sm text-gray-600">
                    Page <span className="font-medium">{msgPage}</span> of{" "}
                    <span className="font-medium">{msgTotalPages}</span>
                  </div>
                  <button
                    onClick={() => goPage(1)}
                    disabled={msgPage === msgTotalPages || msgLoading}
                    className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            !msgLoading && <div className="text-gray-600 text-sm">No messages found.</div>
          )}
        </div>
      </div>
    </section>
  );
}
